import { isEqual } from 'lodash';
import apiClient from '../../../Services/ApiClient';
import { isApiSuccess } from '../../../Utils/http.util';
import { DocumentExtraItemType } from '../../Documents/Enums/documentExtraItemType.enum';

/** How many anamnesis sections (types) are loaded - all of them, there are only a few. */
export const MEDICAL_HISTORY_TYPES_TAKE = 999;

export interface MedicalHistoryItem {
	key: string;
	title: string;
	value: string;
	order: number;
}

interface MedicalHistoryTypeLike {
	value: string;
	name: string;
}

interface MedicalHistoryDocumentItemLike {
	type?: string | null;
	extra?: { type?: string; text?: string } | null;
}

/**
 * Anamnesis sections with the texts saved in the patient's anamnesis document (empty when there is
 * no document yet).
 */
export const buildMedicalHistoryItems = (
	types: MedicalHistoryTypeLike[],
	documentItems?: MedicalHistoryDocumentItemLike[] | null,
): MedicalHistoryItem[] => {
	const texts: Record<string, string> = {};
	documentItems
		?.filter(
			item => item.extra?.type === DocumentExtraItemType.MEDICAL_HISTORY_ITEM,
		)
		.forEach(item => {
			if (item.type) {
				texts[item.type] = item.extra?.text ?? '';
			}
		});

	return types.map((type, order) => ({
		key: type.value,
		title: type.name,
		value: texts[type.value] ?? '',
		order,
	}));
};

interface MedicalHistoryVersionLike {
	parentVersionDocumentId?: string | null;
	updatedAt?: string | null;
}

/** The current version of the anamnesis - the newest one no other version was created from. */
export const latestMedicalHistoryVersion = <
	T extends MedicalHistoryVersionLike,
>(
	versions?: T[] | null,
): T | undefined =>
	versions
		?.filter(version => !version.parentVersionDocumentId)
		.sort(
			(a, b) =>
				new Date(b.updatedAt ?? 0).getTime() -
				new Date(a.updatedAt ?? 0).getTime(),
		)[0];

/** A section of a new version of the anamnesis document (`/document/{id}/copy`). */
export const toMedicalHistoryChild = (
	item: MedicalHistoryItem,
	text: string,
) => ({
	type: item.key,
	extra: {
		title: item.title,
		type: DocumentExtraItemType.MEDICAL_HISTORY_ITEM,
		order: item.order,
		previousText: !isEqual(item.value, text) ? item.value : undefined,
		text,
	},
});

/** Creates the patient's (empty) anamnesis document, returns its id. */
export const createMedicalHistoryDocument = async (args: {
	workplaceId: string;
	patientOriginalId: string;
	createdById: string;
}): Promise<string | undefined> => {
	const response = await apiClient.post('/document/', {
		workplaceId: args.workplaceId,
		userId: args.patientOriginalId,
		createdById: args.createdById,
		type: 'MEDICAL_HISTORY',
		children: [],
	});
	return isApiSuccess(response) ? response.data?.id : undefined;
};
